import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KBIcon } from '@/components/kbIcons';
import { actesSuggestDossier, actesSearch, actesCreateType, type ActesPerson, type ActesRef } from '@/integrations/actesApi';
import { ActesAvatar } from './ActesPerson';
import { actesEn } from '@/lib/actesCountry';

/**
 * The four pickers the reception flow is made of. They are the originals'
 * behaviour, not an approximation of it — a document is filed on the strength
 * of what these three inputs return, so "close enough" here means an act on a
 * stranger's dossier.
 */

/** A document type, searchable, creatable on the fly. Default: auto-detect. */
export function DocTypePicker({ types, value, onChange, compact, lang }: {
  types: ActesRef[]; value: string; onChange: (label: string) => void; compact?: boolean; lang: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const list = useMemo(() => {
    const needle = fold(q.trim());
    const real = types.filter((t) => t.label_fr && t.label_fr.trim() && t.label_fr.trim() !== '/');
    return needle ? real.filter((t) => fold(t.label_fr).includes(needle)) : real;
  }, [types, q]);
  const exact = list.some((t) => fold(t.label_fr) === fold(q.trim()));

  return (
    <div className="actes-pick-anchor">
      <button type="button" className={`actes-picker ${compact ? 'sm' : ''} ${value ? 'set' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span>{value || T('Détection automatique', 'Auto-detect')}</span>
        <KBIcon name="chevron" size={12} />
      </button>
      {open && (
        <>
          <div className="mw-dd-scrim" onClick={() => setOpen(false)} />
          <div className="actes-pick-menu wide">
            <div className="mw-dd-find">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={T('Chercher un type…', 'Search a type…')} />
            </div>
            <button onClick={() => { onChange(''); setOpen(false); }}>{T('Détection automatique', 'Auto-detect')}</button>
            {list.slice(0, 60).map((t) => (
              <button key={t.id} onClick={() => { onChange(t.label_fr); setOpen(false); }}>
                {t.label_fr}{t.label_short_fr ? <em> · {t.label_short_fr}</em> : null}
              </button>
            ))}
            {q.trim().length >= 2 && !exact && (
              <button
                className="create"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const made = await actesCreateType(q.trim());
                    /* Le type était créé et le reste de l'écran ne le savait
                       pas : il n'apparaissait dans aucune autre liste avant un
                       rechargement complet de la page. */
                    await qc.invalidateQueries({ queryKey: ['actes', 'reference'] });
                    onChange(made.data.label_fr);
                    setOpen(false);
                  } finally { setBusy(false); }
                }}
              >
                <KBIcon name="plus" size={12} /> {T(`Créer « ${q.trim()} »`, `Create “${q.trim()}”`)}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Which dossier this belongs to: searched across the whole base by name. */
export function DossierSuggest({ initialQuery, selectedId, selectedName, onPick, lang }: {
  initialQuery?: string; selectedId: string; selectedName?: string;
  onPick: (id: string, name: string) => void; lang: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [q, setQ] = useState(initialQuery ?? '');
  const [rows, setRows] = useState<{ id: string; name: string; hubspot_id: string | null; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const needle = q.trim();
    if (needle.length < 2) { setRows([]); return; }
    timer.current = setTimeout(() => {
      setBusy(true);
      actesSuggestDossier(needle).then(setRows).catch(() => setRows([])).finally(() => setBusy(false));
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  if (selectedId) {
    return (
      <div className="actes-chosen">
        <KBIcon name="check" size={13} />
        <strong>{selectedName || T('Dossier choisi', 'Dossier chosen')}</strong>
        <button onClick={() => onPick('', '')}>{T('changer', 'change')}</button>
      </div>
    );
  }
  return (
    <div className="actes-suggest">
      <label className="actes-find">
        <KBIcon name="search" size={14} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={T('Nom du client ou du dossier…', 'Client or dossier name…')} />
      </label>
      {busy && <p className="muted-note">{T('Recherche…', 'Searching…')}</p>}
      {rows.map((r) => (
        <button key={r.id} className="actes-suggest-row" onClick={() => onPick(r.id, r.name)}>
          <KBIcon name="documents" size={13} />
          <span className="actes-list-main">
            <strong>{r.name}</strong>
            <em>{r.reason}{r.hubspot_id ? ` · ID ${r.hubspot_id}` : ''}</em>
          </span>
          <KBIcon name="chevron-right" size={13} />
        </button>
      ))}
    </div>
  );
}

/** The whole base of requests, when none of the offered targets fit. */
export function TargetSearch({ onPick, lang }: {
  onPick: (acteId: string, dossierId: string | null, hasDoc: boolean, person: string, dossierName: string) => void;
  lang: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<{ id: string; person_concerned: string; dossier_id: string | null; dossier_name?: string | null; file_path: string | null; type_label_fr?: string | null }[]>([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try { setRows(await actesSearch(q.trim(), 'person')); }
    finally { setBusy(false); }
  };

  return (
    <div className="actes-suggest">
      <div className="actes-searchline">
        <label className="actes-find wide">
          <KBIcon name="search" size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
            placeholder={T('Personne concernée, dossier, référence…', 'Person, dossier, reference…')} />
        </label>
        <button className="btn btn-sm" disabled={busy || q.trim().length < 2} onClick={() => void run()}>
          {T('Chercher', 'Search')}
        </button>
      </div>
      {rows.slice(0, 12).map((r) => (
        <button key={r.id} className="actes-suggest-row"
          onClick={() => onPick(r.id, r.dossier_id, !!r.file_path, r.person_concerned, r.dossier_name ?? '')}>
          <KBIcon name="user" size={13} />
          <span className="actes-list-main">
            <strong>{r.person_concerned}</strong>
            <em>{r.dossier_name || T('sans dossier', 'no dossier')}{r.type_label_fr ? ` · ${r.type_label_fr}` : ''}</em>
          </span>
          {r.file_path && <KBIcon name="documents" size={12} />}
        </button>
      ))}
    </div>
  );
}

/** Who is responsible, when a request is created with no dossier to inherit
 *  one from — those people, and only those, are the ones told about it. */
export function ResponsiblePicker({ people, selected, onChange, lang }: {
  people: ActesPerson[]; selected: string[]; onChange: (ids: string[]) => void; lang: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [open, setOpen] = useState(false);
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="actes-picks">
      {people.filter((p) => selected.includes(p.id)).map((p) => (
        <span key={p.id} className="actes-pick">
          <ActesAvatar person={p} />
          {p.full_name ?? p.email}
          <button onClick={() => toggle(p.id)} aria-label="remove">×</button>
        </span>
      ))}
      <div className="actes-pick-anchor">
        <button className="actes-pick-add" onClick={() => setOpen((o) => !o)}>
          <KBIcon name="plus" size={12} /> {T('Ajouter', 'Add')}
        </button>
        {open && (
          <>
            <div className="mw-dd-scrim" onClick={() => setOpen(false)} />
            <div className="actes-pick-menu">
              {people.filter((p) => !selected.includes(p.id)).map((p) => (
                <button key={p.id} onClick={() => { toggle(p.id); setOpen(false); }}>{p.full_name ?? p.email}</button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
